import React, { useEffect, useState } from "react";
import { Offcanvas, Button, Form } from "react-bootstrap";
import { DEFAULT_YOUTUBE_CATEGORIES } from "@/utils/constants/uiConstants";
import { YtMark } from "./playlistShared";

/* ───────────── YouTube publish sheet — mirrors the real YouTube social card's fields ───────────── */
const CATEGORY_OPTIONS = Object.keys(DEFAULT_YOUTUBE_CATEGORIES);
const VISIBILITY_OPTIONS = ["Public", "Unlisted", "Private"];
const MONETIZATION_OPTIONS = ["Monetize with ads", "Limited ads", "Don’t monetize"];

const PublishSheet: React.FC<{
  channelName: string;
  targetPlaylists: string[];
  mediaTitle: string;
  show: boolean;
  onClose: () => void;
  onPublished: () => void;
  onOpenSocialPublish?: () => void;
}> = ({ channelName, targetPlaylists, mediaTitle, show, onClose, onPublished, onOpenSocialPublish }) => {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("Public");
  const [category, setCategory] = useState("");
  const [kids, setKids] = useState("");
  const [monetization, setMonetization] = useState(MONETIZATION_OPTIONS[0]);
  const [rights, setRights] = useState("yes");
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!show) return;
    setTitle(mediaTitle || "");
    setDesc("");
    setTags("");
    setVisibility("Public");
    setCategory("");
    setKids("");
    setMonetization(MONETIZATION_OPTIONS[0]);
    setRights("yes");
    setTried(false);
  }, [show, mediaTitle]);

  const errTitle = !title.trim();
  const errCategory = !category;
  const errKids = !kids;
  const invalid = errTitle || errCategory || errKids;

  const publish = () => {
    setTried(true);
    if (invalid) return;
    onPublished(); // mock — dev wires the real YouTube/social publish here
    onClose();
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      container={typeof document !== "undefined" ? document.body : undefined}
      style={{ width: 460 }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="fs-6">
          <span className="d-inline-flex align-items-center gap-2"><YtMark /> Publish to YouTube · {channelName}</span>
          <div className="small text-muted mt-1">Required to add this video to the {channelName} playlist</div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold mb-1">Title</Form.Label>
          <Form.Control value={title} isInvalid={tried && errTitle} onChange={(e) => setTitle(e.target.value)} />
          <Form.Control.Feedback type="invalid">Title is required.</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold mb-1">Description</Form.Label>
          <Form.Control as="textarea" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold mb-1">Tags</Form.Label>
          <Form.Control value={tags} placeholder="news, highlights" onChange={(e) => setTags(e.target.value)} />
        </Form.Group>

        <div className="row g-2 mb-3">
          <Form.Group className="col-6">
            <Form.Label className="small fw-semibold mb-1">Visibility</Form.Label>
            <Form.Select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              {VISIBILITY_OPTIONS.map((v) => <option key={v}>{v}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="col-6">
            <Form.Label className="small fw-semibold mb-1">Category</Form.Label>
            <Form.Select value={category} isInvalid={tried && errCategory} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Select a category…</option>
              {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid">Pick a category.</Form.Control.Feedback>
          </Form.Group>
        </div>

        <div className="row g-2 mb-3">
          <Form.Group className="col-6">
            <Form.Label className="small fw-semibold mb-1">Made for kids</Form.Label>
            <Form.Select value={kids} isInvalid={tried && errKids} onChange={(e) => setKids(e.target.value)}>
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">Select an option.</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="col-6">
            <Form.Label className="small fw-semibold mb-1">Rights management</Form.Label>
            <Form.Select value={rights} onChange={(e) => setRights(e.target.value)}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Form.Select>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold mb-1">Monetization</Form.Label>
          <Form.Select value={monetization} onChange={(e) => setMonetization(e.target.value)}>
            {MONETIZATION_OPTIONS.map((m) => <option key={m}>{m}</option>)}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold mb-1">Add to playlist</Form.Label>
          <div className="border rounded p-2 d-flex align-items-center gap-2 small">
            <YtMark /> <b>{targetPlaylists.join(", ")}</b> <span className="text-muted">· position 1 (top)</span>
          </div>
        </Form.Group>

        {onOpenSocialPublish && (
          <Button variant="link" className="p-0 small text-decoration-none" onClick={onOpenSocialPublish}>
            Open full card in Social Publish →
          </Button>
        )}
      </Offcanvas.Body>
      <div className="p-3 border-top">
        <Button className="w-100" onClick={publish}>Publish to YouTube</Button>
      </div>
    </Offcanvas>
  );
};

export default PublishSheet;
